import type { ReactNode } from "react";
import { useAccount } from "wagmi";
import { EthTokenIcon, GameProvider, Text, TokenYoloIcon, WinnerCrownIcon } from "@looksrare/uikit";
import { ChainId } from "@looksrare/config";
import {
  ContractVersion,
  YoloConfig,
  YoloContractName,
  getYoloContractNameFromNetworkAndVersion,
} from "@looksrare/yolo";
import { WarningModal } from "@looksrare/yolo/components/modals/WarningModal";
import { PotItemsList } from "@looksrare/yolo/components/round/PotItemsList";
import { AddErc20 } from "@looksrare/yolo/components/depositAssets/AddPanel/AddErc20";
import { StepSendToPotsMultipleRound } from "@looksrare/yolo/components/modals/EnterYoloModal/StepSendToPotsMultipleRounds";
import { GemMessage } from "@looksrare/yolo/components/round/points/GemMessage";
import { isBefore } from "date-fns/isBefore";
import { Help } from "@/components/gamesCustomisations/yolo/Help";
import { gameAddresses } from "@/config";
import { CenterActiveMain } from "@/components/gamesCustomisations/yolo/CenterActiveMain";
import { YoloPointsExplanationModal } from "@/components/gamesCustomisations/yolo/YoloPointsExplanationModal";
import { getUserColor } from "@/components/gamesCustomisations/yolo/colors";
import { useGetClaimPrizesPaymentRequired } from "@/components/gamesCustomisations/yolo/contract";
import { PlayerViewProfileCTA } from "@/components/gamesCustomisations/yolo/PlayerViewProfileCTA";
import {
  useDeposit,
  useDepositEthIntoMultipleRounds,
  useClaimPrizes,
  useReadOracleSignatureValidityPeriod,
  useRollOverEthToNextRound,
  useWithdrawDeposits,
} from "@/components/gamesCustomisations/yolo-limited/utils/contract";
import { useUser } from "@/queries/user";
import { useGlobalSettings } from "@/hooks";
import { CenterWinnerAvatar } from "./gamesCustomisations/yolo/CenterWinnerAvatar";
import { LimitedModeExplanationCard } from "./gamesCustomisations/yolo-limited/LimitedModeExplanationCard";

export const YoloGamesYoloLimitedProvider = ({ children }: { children: ReactNode }) => {
  const isSoundEnabled = useGlobalSettings((state) => state.isSoundEnabled);
  const { address } = useAccount();
  const { data: user } = useUser(address!, { enabled: !!address });

  return (
    <GameProvider<YoloConfig>
      widgetBotOptions={{
        server: "1204359490925764680",
        channel: "1204997140297027696",
        shard: "https://emerald.widgetbot.io",
        glyph: ["/images/win/chat.png", "32px"],
        color: "#CCFD07",
        notifications: false,
        indicator: true,
      }}
      supportedNetworks={["blast"]}
      addresses={gameAddresses}
      fallbackChainId={ChainId.BLAST_MAINNET}
      config={{
        basePath: "yolo-limited",
        referenceToken: {
          symbol: "ETH",
          icon: EthTokenIcon,
          decimals: 18,
          name: "Ethereum",
          minDeposit: 0.001,
          maxDeposit: 0.1,
          maxDisplayDecimals: 6,
          maxPrecision: 3,
        },
        getUserColor,
        hasBackgroundImage: true,
        // Accounts for left nav
        // Note: caused by breakpoint mismatch
        mobileStickyBarProps: { width: { base: "100%", md: "calc(100% - 5rem)" }, right: 0, left: undefined },
        supportedTokens: ["ETH", "YOLO", "BLOOKS", "USDB", "BLAST", "PAC", "ORBIT"],
        useGlobalMuteButton: true,
        supportsMetrics: true,
        isRolloverEnabled: true,
        depositPresets: ["0.001", "0.01", "0.1"],
        points: {
          enabled: false,
          bonusPoints: 500,
          hideEnterNowMultiplier: false,
          hideMultiplierInRoundOverview: true,
          label: "Pts",
          Icon: null,
          accentColor: "interactive-03",
          bgGradient: "transparent",
        },
        roulette: {
          CenterActiveMain,
        },
        round: {
          ctaArea: {
            props: {
              borderColor: "interactive-03",
              borderWidth: "1px",
            },
          },
        },
        EyeCatchingText: Text,
        WarningModal,
        supportNfts: false,
        supportErc20: true,
        supportNativeToken: true,
        removeRoundCtaBg: true,
        supportsUserProfiles: false,
        hideLogoInCart: true,
        defaultDepositContractName: "ETH",
        TransferManagerIcon: TokenYoloIcon,
        contract: {
          version: ContractVersion.V1_LIMITED,
          // This project uses all defaults
          identifier: "YOLO_V1_LIMITED",
          useDeposit,
          useDepositEthIntoMultipleRounds,
          useClaimPrizes,
          useReadOracleSignatureValidityPeriod,
          useGetClaimPrizesPaymentRequired,
          useRollOverEthToNextRound,
          useWithdrawDeposits,
          getYoloContractNameFromNetworkAndVersion,
          getYoloContractNamesFromNetwork: () => [YoloContractName.YOLO_LIMITED_V1_BLAST],
        },
        AddErc20,
        PotItemsList,
        StepSendToPotsMultipleRound,
        GemMessage,
        YoloPointsExplanationModal,
        Help,
        WinnerIcon: WinnerCrownIcon,
        CenterWinnerAvatar,
        PlayerViewProfileCTA,
        GameExplanationCard: LimitedModeExplanationCard,
        selfTimeoutUntil: user?.selfTimeoutUntil,
        isWalletBlocked: !!user?.selfTimeoutUntil && isBefore(Date.now(), user.selfTimeoutUntil),
        isMuted: !isSoundEnabled,
      }}
    >
      {children}
    </GameProvider>
  );
};
